import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../redux/hooks";
import { loginUser } from "../../redux/api/_admin.api";
import parseToken from "src/utils/jwt";
import { logout } from "src/redux/reducers/authSlice";
import { LoaderCircle } from "lucide-react";

interface LoginProps {}

export const Login: React.FC<LoginProps> = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const redirectRouteByRole = (roles: string[]): string | null => {
    if (roles.includes("ADMIN")) return "/admin";
    if (roles.includes("PROCTOR")) return "/proctor";
    if (roles.includes("FRONT_DESK")) return "/front-desk";
    if (roles.includes("HRWO")) return "/hr";
    if (roles.includes("SO")) return "/so";
    return null;
  };

  const handleLogin = async () => {
    setLoading(true);
    try {
      const response = await dispatch(loginUser(email, password));
      if (response) {
        const decoded = parseToken(response as string);
        const roles = decoded?.["cognito:groups"] || [];

        const route = redirectRouteByRole(roles);

        if (route) {
          navigate(route);
        } else {
          toast.error("You do not have access to this application.");
          dispatch(logout());
          navigate("/login");
        }
      }
    } catch (error: any) {
      console.error("Invalid email or password", error);
      toast.error(error?.message || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-6 py-12 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <h2 className="mt-10 text-center text-2xl font-bold tracking-tight text-gray-900">
          Sign in to your Admin Account
        </h2>
      </div>
      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        <div className="space-y-6">
          <div className="mt-2 flex justify-center">
            <div className="w-fit">
              <label
                htmlFor="email"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Email address
              </label>
              <input
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                required
                placeholder="Your email"
                className="input w-[320px] max-w-xs rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>

          <div>
            <div className="mt-2 flex justify-center">
              <div className="w-fit">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Password
                </label>
                <input
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  required
                  placeholder="Your Password"
                  className="input w-[320px] max-w-xs rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <button
              onClick={handleLogin}
              disabled={loading}
              className="btn btn-wide max-w-xs bg-yellow-500 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              {loading ? <LoaderCircle /> : "Sign in"}
            </button>
          </div>
        </div>
      </div>
      <div className="my-8">
        <a href="/forgot-password">
          <h1 className="text-blue-400 text-lg text-center hover:text-blue-500 hover:font-semibold transition-all">
            Forgot Password
          </h1>
        </a>
      </div>
    </div>
  );
};
